import React, { Suspense, lazy, useContext, useEffect } from 'react';
import { Redirect, Route, RouteProps, Switch, useHistory, useLocation } from 'react-router-dom';
import ActivateAccount from '../components-v2/ActivateAccount';
import ActivatePlan from '../components-v2/ActivatePlan';
import AdminProvider from '../components-v2/Admin/AdminProvider';
import CheckoutSuccessful from '../components-v2/CheckoutSuccessful';
import ErrorBoundary, { ErrorSlate } from '../components-v2/ErrorBoundary';
import ForgotPassword from '../components-v2/ForgotPassword';
import Invoice from '../components-v2/Invoice';
import Page from '../components-v2/Layout/Page';
import LoadingComponent from '../components-v2/LoadingComponent';
import Login from '../components-v2/Login';
import McpAuthorize from '../components-v2/McpAuthorize';
import { showMetamaskPromoModal } from '../components-v2/MetamaskPromo/MetamaskPromoModal';
import { METAMASK_PROMO_COOKIE } from '../components-v2/MetamaskPromo/consts';
import PageNotFound from '../components-v2/PageNotFound';
import ResetPassword from '../components-v2/ResetPassword';
import Signup from '../components-v2/Signup';
import VerifyEmail from '../components-v2/VerifyEmail';
import { UserContext } from '../contexts';
import { usePrevious } from '../hooks/usePrevious';
import Auth from '../lib/Auth';
import { getCookie } from '../lib/Auth/getCookie';
import { TransactionsExplorerRoute } from '../navigation/transactionsLink/TransactionsExplorerRoute';
import { ROUTES } from '../routes';

const ReconProfile = lazy(() => import('../components-v2/ReconProfile'));
const SelfRecon = lazy(() => import('../components-v2/SelfRecon'));
const Holdings = lazy(() => import('../components-v2/Holdings'));
const LegacyHoldingsRedirect = lazy(() => import('../components-v2/Holdings/LegacyRedirect'));
const GettingStartedWithVIP = lazy(() => import('../components-v2/GettingStartedWithVIP'));
const Wizard = lazy(() => import('./Wizard'));
const Purchase = lazy(() => import('../components-v2/Purchase'));
const Documents = lazy(() => import('../components-v2/DocumentCenter'));
const Settings = lazy(() => import('../components-v2/Settings'));
const ClientDashboard = lazy(() => import('./Organization/Clients'));
const Imports = lazy(() => import('../components-v2/Imports'));
const ManualEntry = lazy(() => import('../components-v2/AllTransactions/ManualEntry'));
const Admin = lazy(() => import('../components-v2/Admin'));
const CreateInvoice = lazy(() => import('../components-v2/CreateInvoice'));
const IncomeDashboard = lazy(() => import('../components-v2/IncomeDashboard'));
const TaxDashboardV2 = lazy(() => import('../components-v2/TaxDashboard/container'));
const TaxGuide = lazy(() => import('../components-v2/TaxGuide'));
const Typeform = lazy(() => import('../components-v2/Typeform'));

const isOrgOwnerOrAdmin = (role?: string) => role === 'org-owner' || role === 'org-admin';

type ProtectedRouteProps = {
  permit: boolean;
} & RouteProps;

const ProtectedRoute = ({ permit, ...props }: ProtectedRouteProps) => {
  if (permit) return <Route {...props} />;

  return <Redirect to="/" />;
};

const isAllowedRedirect = (url: string) => {
  try {
    const parsed = new URL(url);
    if (!parsed.pathname.startsWith('/oauth/') && parsed.pathname !== '/mcp/authorize') return false;

    const host = parsed.hostname.toLowerCase();
    const isLoopback = host === 'localhost' || host === '127.0.0.1';
    const isSameOrigin = parsed.origin === window.location.origin;

    return isLoopback || isSameOrigin;
  } catch {
    return false;
  }
};

const LoginRedirect = () => {
  const { search } = useLocation();
  const redirectTo = new URLSearchParams(search).get('redirect');

  if (redirectTo && isAllowedRedirect(redirectTo)) {
    window.location.href = redirectTo;
    return null;
  }

  return <Redirect to="/" />;
};

const Routes = () => {
  const history = useHistory();
  const { currentUserRole, isLoggedIn } = useContext(UserContext);
  const prevUserRole = usePrevious(currentUserRole);

  // when we hijack a user, we don't know from their userId alone if they're an org admin
  // so by default we redirect them from `/home` to the tax dashboard
  // once we're done fetching the user (which happens asynchronously while the dashboard is loaded)
  // if we find out the user is actually an org admin, redirect them to the clients page.
  // this is an acceptable tradeoff between speed in most cases (loading the user async)
  // and a quick "flash" in the rare scenario where admins hijack org admins
  //
  useEffect(() => {
    if (prevUserRole === 'user' && isOrgOwnerOrAdmin(currentUserRole)) {
      history.replace('/clients');
    }
  }, [currentUserRole, history, prevUserRole]);

  const tokenIsForAdmin = Auth.tokenIsForAdmin();
  const userIsUser = (isLoggedIn && currentUserRole === 'user') || currentUserRole === 'org-user';
  const userIsOrgOwnerOrAdmin = Boolean(isLoggedIn && isOrgOwnerOrAdmin(currentUserRole));

  useEffect(() => {
    let isModalOpen = false;
    const cookieValue = getCookie(METAMASK_PROMO_COOKIE);
    if (cookieValue && !isModalOpen) {
      showMetamaskPromoModal({ cookieValue });
      isModalOpen = true;
    }
    return () => {
      isModalOpen = false;
    };
  }, [isLoggedIn]);

  if (!isLoggedIn) {
    return (
      <Route
        path="/*"
        render={() => {
          if (Auth.getHijackedUserId()) {
            // we're not logged in but there is a hijackedUserId in the URL
            // most likely an error caused by pasting a hijacked-state URL while logged out
            // remove the parameter before continuing
            setTimeout(Auth.unsetHijackedUserIdSearchParam); // setTimeout as this is a side-effect which render in theory shouldn't have
            return;
          }

          return (
            <Switch>
              <Route path="/consultation">
                <Typeform />
              </Route>
              <Route path="/login">
                <Login />
              </Route>
              <Route exact path="/signup">
                <Signup />
              </Route>
              <Route exact path="/forgot">
                <ForgotPassword />
              </Route>
              <Route exact path="/reset/:token">
                <ResetPassword />
              </Route>
              <Route exact path="/invoice">
                <Invoice />
              </Route>
              <Route exact path="/activate">
                <ActivateAccount />
              </Route>
              <Route exact path="/verify-email/:token">
                <VerifyEmail />
              </Route>
              <Route exact path="/activate-plan">
                <ActivatePlan />
              </Route>
              <Route exact path={ROUTES.checkoutSuccessful}>
                <CheckoutSuccessful />
              </Route>
              <Route exact path="/mcp/authorize">
                <Redirect to={`/login?redirect=${encodeURIComponent(window.location.href)}`} />
              </Route>
              <Route path="/*">
                <Redirect to="/login" />
              </Route>
            </Switch>
          );
        }}
      />
    );
  }

  return (
    <Switch>
      <Route exact path="/consultation">
        <Typeform />
      </Route>

      <Route exact path="/invoice">
        <Invoice />
      </Route>

      <Route exact path="/activate">
        <ActivateAccount />
      </Route>

      <Route exact path="/verify-email/:token">
        <VerifyEmail />
      </Route>

      <Route>
        <ErrorBoundary
          fallback={
            <div className="container m-auto">
              <ErrorSlate linkTo={ROUTES.imports} message="Issue loading component" />
            </div>
          }
        >
          <Suspense
            fallback={
              <Page>
                <LoadingComponent />
              </Page>
            }
          >
            <Switch>
              <Route path="/*">
                <Switch>
                  <Route path="/login">
                    <LoginRedirect />
                  </Route>
                  <Route exact path="/mcp/authorize">
                    <McpAuthorize />
                  </Route>
                  <Route path="/signup">
                    <Redirect to="/" />
                  </Route>
                  <Route exact path="/forgot">
                    <Redirect to="/" />
                  </Route>
                  <Route exact path="/reset/:token">
                    <Redirect to="/" />
                  </Route>
                  <Route exact path="/activate-plan">
                    <ActivatePlan />
                  </Route>
                  <Route exact path={ROUTES.checkoutSuccessful}>
                    <CheckoutSuccessful />
                  </Route>
                  <Route exact path="/consultation">
                    <Typeform />
                  </Route>

                  <ProtectedRoute
                    exact
                    path={['/home', '/']}
                    permit={isLoggedIn}
                    render={() => {
                      switch (currentUserRole) {
                        case 'tokentax-admin':
                          return <Redirect to="/tokentax-admin" />;
                        case 'org-owner':
                        case 'org-admin':
                          return <Redirect to="/clients" />;
                        case 'user':
                        default:
                          return <Redirect to="/dashboard" />;
                      }
                    }}
                  />
                  {/* TOKENTAX ADMIN ROUTES (/invoice/admin is a nested route, look for uses of <InvoiceAdmin />) */}
                  <ProtectedRoute path={['/tokentax-admin', '/invoice/admin']} permit={tokenIsForAdmin}>
                    <AdminProvider>
                      <Admin />
                    </AdminProvider>
                  </ProtectedRoute>
                  <ProtectedRoute path="/clients" permit={userIsOrgOwnerOrAdmin}>
                    <ClientDashboard />
                  </ProtectedRoute>
                  <ProtectedRoute path="/invoice/create/:userId?" permit={tokenIsForAdmin}>
                    <CreateInvoice />
                  </ProtectedRoute>
                  {/* MAIN USER ROUTES*/}
                  <ProtectedRoute path="/*" permit={userIsUser}>
                    <Switch>
                      <Route exact path="/dashboard">
                        <TaxDashboardV2 />
                      </Route>
                      <Route exact path={ROUTES.manualEntry}>
                        <ManualEntry />
                      </Route>
                      <Route path="/all-transactions">
                        <TransactionsExplorerRoute page="legacy" />
                      </Route>
                      <Route path="/all-transfers">
                        <TransactionsExplorerRoute page="transferGroups" />
                      </Route>

                      <Route path={ROUTES.holdings}>
                        <Holdings />
                      </Route>
                      <Route path={['/tax-loss-harvesting', '/portfolio', '/recon/cost-basis/balances']}>
                        <LegacyHoldingsRedirect />
                      </Route>
                      <Route path="/income">
                        <IncomeDashboard />
                      </Route>
                      <Route path="/tax-guide">
                        <TaxGuide />
                      </Route>
                      <Route path="/profile">
                        <ReconProfile />
                      </Route>

                      <Route path="/getting-started-with-vip">
                        <GettingStartedWithVIP />
                      </Route>
                      <Route path="/start">
                        <div className="container m-auto">
                          <Wizard />
                        </div>
                      </Route>
                      <Route path={[ROUTES.imports, ROUTES.importsAdd, ROUTES.importsEdit]} exact>
                        <Imports />
                      </Route>
                      <Route path={ROUTES.documents}>
                        <Documents />
                      </Route>
                      <Route path="/purchase">
                        <Purchase />
                      </Route>
                      <Route path="/pay">
                        <Redirect to="/purchase" />
                      </Route>
                      <Route path="/settings">
                        <Settings />
                      </Route>
                      <Route path="/recon*">
                        <SelfRecon />
                      </Route>
                      <Route path="*">
                        <PageNotFound />
                      </Route>
                    </Switch>
                  </ProtectedRoute>
                  <Route path="*">{isLoggedIn ? <PageNotFound /> : <></>}</Route>
                </Switch>
              </Route>
            </Switch>
          </Suspense>
        </ErrorBoundary>
      </Route>
    </Switch>
  );
};

export default Routes;
